import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { DESIGNATIONS, type Designation } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserAvatar } from '@/components/ui/avatar';
import { TimezonePicker } from '@/components/shared/TimezonePicker';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth';
import { useUiStore, type Density, type Theme } from '@/stores/ui';
import { useUpdateUser, useWorkspace } from './api';

function humanise(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ProfilePage() {
  const { user, refreshMe } = useAuth();
  const update = useUpdateUser();
  const theme = useUiStore((s) => s.theme);
  const density = useUiStore((s) => s.density);
  const setTheme = useUiStore((s) => s.setTheme);
  const setDensity = useUiStore((s) => s.setDensity);

  const { data: workspace } = useWorkspace();

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [designation, setDesignation] = useState<Designation>('developer');
  const [capacity, setCapacity] = useState(35);
  const [skills, setSkills] = useState('');

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setTimezone(user.timezone);
    setDesignation(user.designation as Designation);
  }, [user]);

  if (!user) return null;

  async function save() {
    try {
      await update.mutateAsync({
        userId: user!._id,
        patch: {
          name: name.trim(),
          timezone,
          designation,
          capacityHoursPerWeek: capacity,
          skills: skills
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean),
        },
      });
      await refreshMe();
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save your profile');
    }
  }

  return (
    <>
      <PageHeader title="Profile" subtitle="Your details, capacity and how the app looks." />
      <div className="max-w-[560px] p-4">
        <div className="vt-surface flex flex-col gap-3 p-4">
          <div className="flex items-center gap-3 pb-1">
            <UserAvatar id={user._id} name={user.name} src={user.avatarUrl} size={44} />
            <div>
              <div className="text-[15px] font-medium">{user.name}</div>
              <div className="text-[12px] vt-muted">{user.email}</div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="designation">Designation</Label>
              <select
                id="designation"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={designation}
                onChange={(e) => setDesignation(e.target.value as Designation)}
              >
                {DESIGNATIONS.map((d) => (
                  <option key={d} value={d}>
                    {humanise(d)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="capacity">Capacity (hrs/week)</Label>
              <Input
                id="capacity"
                type="number"
                min={0}
                max={80}
                value={capacity}
                onChange={(e) => setCapacity(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="timezone">Timezone</Label>
            {/* V10 §4 — a list, never a text box. The workspace's zone is
                offered first because most people share it. */}
            <TimezonePicker
              id="timezone"
              value={timezone}
              onChange={setTimezone}
              suggested={workspace?.timezone}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="skills">Skills (comma separated)</Label>
            <Input
              id="skills"
              value={skills}
              placeholder="flutter, node, figma, aso"
              onChange={(e) => setSkills(e.target.value)}
            />
            <p className="text-[11px] vt-muted">Used to suggest assignees for matching work.</p>
          </div>

          <div className="flex justify-end pt-1">
            <Button variant="primary" data-testid="profile-save" onClick={save} disabled={update.isPending}>
              {update.isPending ? <Loader2 className="animate-spin" /> : null}
              Save profile
            </Button>
          </div>
        </div>

        <div className="vt-surface mt-3 flex flex-col gap-3 p-4">
          <h2 className="text-[15px] font-semibold">Appearance</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="theme">Theme</Label>
              <select
                id="theme"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={theme}
                onChange={(e) => setTheme(e.target.value as Theme)}
              >
                <option value="system">Match system</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="density">Density</Label>
              <select
                id="density"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={density}
                onChange={(e) => setDensity(e.target.value as Density)}
              >
                <option value="comfortable">Comfortable</option>
                <option value="compact">Compact</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
